import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, DatePicker } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { getAnalyticsTableData, downloadAnalyticsTableExcel } from '../../api';
import Toast from '../Toast';
import './AnalyticsPage.css';

dayjs.locale('ru');

const AnalyticsTable = () => {
    // Текущий день и дата 30 дней назад — используются как диапазон по умолчанию для таблицы
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    // Форматирование даты в YYYY-MM-DD (локально, без учета часового пояса)
    const formatDate = (date) => date.toISOString().split('T')[0];

    // Диапазон по умолчанию для таблицы: последние 30 дней
    const defaultStartDate = formatDate(thirtyDaysAgo);
    const defaultEndDate = formatDate(today);

    // Текущий выбранный диапазон дат для таблицы аналитики
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    // Строки таблицы аналитики
    const [rows, setRows] = useState([]);
    // Флаги загрузки и выгрузки
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    // Состояние тостов
    const [toastMessage, setToastMessage] = useState(null);
    const [toastType, setToastType] = useState('');
    // Конфигурация сортировки таблицы
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    // Текст фильтра по названию кластера/завода
    const [filterText, setFilterText] = useState('');
    // Флаг активности фильтра (для визуального оформления)
    const [isFilterActive, setIsFilterActive] = useState(false);
    // Флаг того, что диапазон дат был восстановлен из localStorage и не совпадает с дефолтным
    const [isRestoredFromStorage, setIsRestoredFromStorage] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        // При монтировании пробуем восстановить диапазон дат из localStorage,
        // если он был сохранен не более часа назад
        try {
            const stored = localStorage.getItem('analyticsDateRange');
            if (stored) {
                const parsed = JSON.parse(stored);
                const { startDate: storedStart, endDate: storedEnd, savedAt } = parsed || {};

                if (storedStart && storedEnd && savedAt) {
                    const ageMs = Date.now() - savedAt;
                    const oneHourMs = 60 * 60 * 1000;

                    if (ageMs <= oneHourMs) {
                        setStartDate(storedStart);
                        setEndDate(storedEnd);

                        const isDefaultRange =
                            storedStart === defaultStartDate &&
                            storedEnd === defaultEndDate;

                        setIsRestoredFromStorage(!isDefaultRange);
                    }
                }
            }
        } catch (e) {
            console.error('Не удалось восстановить диапазон дат аналитики из localStorage', e);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // При монтировании пробуем восстановить текст фильтра по заводам/кластерам из localStorage
        try {
            const stored = localStorage.getItem('analyticsFilterText');
            if (stored) {
                const parsed = JSON.parse(stored);
                const { value, savedAt } = parsed || {};

                if (typeof value === 'string' && savedAt) {
                    const ageMs = Date.now() - savedAt;
                    const oneHourMs = 60 * 60 * 1000;

                    if (ageMs <= oneHourMs && value.trim()) {
                        setFilterText(value);
                        setIsFilterActive(true);
                    }
                }
            }
        } catch (e) {
            console.error('Не удалось восстановить фильтр аналитики из localStorage', e);
        }
    }, []);

    useEffect(() => {
        // Автоматическая перезагрузка данных таблицы при изменении фильтра или диапазона дат
        // с небольшой задержкой (debounce), чтобы не слать слишком много запросов
        const timeoutId = setTimeout(() => {
            handleLoad();
        }, 1000);

        return () => {
            clearTimeout(timeoutId);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterText, startDate, endDate]);

    // Применить новый диапазон дат и сохранить его в localStorage
    const applyAndPersistDateRange = (nextStart, nextEnd) => {
        setStartDate(nextStart);
        setEndDate(nextEnd);

        const isDefaultRange =
            nextStart === defaultStartDate &&
            nextEnd === defaultEndDate;

        try {
            localStorage.setItem(
                'analyticsDateRange',
                JSON.stringify({
                    startDate: nextStart,
                    endDate: nextEnd,
                    savedAt: Date.now(),
                })
            );
        } catch (e) {
            console.error('Не удалось сохранить диапазон дат аналитики в localStorage', e);
        }

        setIsRestoredFromStorage(!isDefaultRange);
    };

    // Сдвиг диапазона дат на полный предыдущий/следующий месяц
    const shiftMonth = (direction) => {
        // В качестве опорной даты берем конец текущего диапазона (если есть),
        // иначе начало диапазона, иначе дату по умолчанию
        const baseDate = endDate || startDate || defaultEndDate;

        const [yearStr, monthStr] = baseDate.split('-');
        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10) - 1;

        const currentMonthDate = new Date(year, month, 1);

        const targetMonthDate = new Date(
            currentMonthDate.getFullYear(),
            currentMonthDate.getMonth() + (direction === 'prev' ? -1 : 1),
            1
        );

        const targetYear = targetMonthDate.getFullYear();
        const targetMonth = targetMonthDate.getMonth();

        // Первый и последний день целевого месяца
        const monthStart = new Date(targetYear, targetMonth, 1);
        const monthEnd = new Date(targetYear, targetMonth + 1, 0);

        const formatLocalDate = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };

        const nextStart = formatLocalDate(monthStart);
        const nextEnd = formatLocalDate(monthEnd);

        applyAndPersistDateRange(nextStart, nextEnd);
    };

    // Построение объекта с параметрами запроса для таблицы аналитики
    const buildQueryParams = (overrides = {}) => {
        const params = {
            startDate,
            endDate,
        };

        const normalizedFilter = filterText.trim();
        if (normalizedFilter) {
            params.factoryNameFilter = normalizedFilter;
        }

        // Параметры сортировки: берем либо из overrides, либо из текущей конфигурации
        const sortColumn =
            overrides.sortColumn === undefined ? sortConfig.key : overrides.sortColumn;
        const sortDirection =
            overrides.sortDirection === undefined ? sortConfig.direction : overrides.sortDirection;

        if (sortColumn && sortDirection) {
            params.sortColumn = sortColumn;
            params.sortDirection = sortDirection;
        }

        return params;
    };

    // Загрузка данных таблицы аналитики с учетом фильтров, сортировки и диапазона дат
    const handleLoad = async (overrides = {}) => {
        if (!startDate || !endDate) {
            setToastMessage('Укажите обе даты диапазона');
            setToastType('error');
            return;
        }
        if (startDate > endDate) {
            setToastMessage('Дата начала не может быть больше даты окончания');
            setToastType('error');
            return;
        }
        const queryParams = buildQueryParams(overrides);

        setLoading(true);
        try {
            const data = await getAnalyticsTableData(queryParams);
            setRows(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Ошибка при загрузке таблицы аналитики', error);
            setToastMessage('Ошибка при загрузке данных таблицы');
            setToastType('error');
        } finally {
            setLoading(false);
        }
    };

    // Переход на страницу дневных отчетов по клику на завод
    const handleFactoryClick = (factoryId) => {
        if (!factoryId || !startDate) {
            return;
        }

        navigate(`/manager/daily-reports?factoryId=${factoryId}&date=${startDate}`);
    };

    // Обработка клика по заголовку столбца для сортировки
    const requestSort = (key) => {
        let nextDirection = 'asc';

        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            nextDirection = 'desc';
        } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
            nextDirection = null;
        }

        const nextSortConfig = {
            key: nextDirection ? key : null,
            direction: nextDirection,
        };

        setSortConfig(nextSortConfig);

        const overrides = {};
        if (nextSortConfig.key && nextSortConfig.direction) {
            overrides.sortColumn = nextSortConfig.key;
            overrides.sortDirection = nextSortConfig.direction;
        } else {
            overrides.sortColumn = null;
            overrides.sortDirection = null;
        }

        handleLoad(overrides);
    };

    // Текстовый индикатор направления сортировки в заголовке столбца
    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? '▲' : '▼';
    };

    // Выгрузка таблицы аналитики в Excel за выбранный период
    const handleExport = async () => {
        if (!startDate || !endDate) {
            setToastMessage('Укажите обе даты диапазона');
            setToastType('error');
            return;
        }
        if (startDate > endDate) {
            setToastMessage('Дата начала не может быть больше даты окончания');
            setToastType('error');
            return;
        }
        const queryParams = buildQueryParams();

        setExporting(true);
        try {
            await downloadAnalyticsTableExcel(queryParams);
        } catch (error) {
            console.error('Ошибка при выгрузке таблицы аналитики', error);
            setToastMessage('Ошибка при выгрузке таблицы аналитики');
            setToastType('error');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="analytics-table__container">
            <div className="analytics-table__controls">
                <div
                    className={
                        `analytics-table__date-range${
                            isRestoredFromStorage ? ' analytics-table__date-range--restored' : ''
                        }`
                    }
                >
                    <button
                        type="button"
                        className="analytics-table__button"
                        onClick={() => shiftMonth('prev')}
                    >
                        ◀
                    </button>
                    <span className="analytics-table__date-range-label">Диапазон дат:</span>
                    <ConfigProvider locale={ruRU}>
                        <DatePicker.RangePicker
                            value={
                                startDate && endDate
                                    ? [dayjs(startDate, 'YYYY-MM-DD'), dayjs(endDate, 'YYYY-MM-DD')]
                                    : null
                            }
                            onChange={(values) => {
                                if (!values || values.length !== 2) {
                                    setStartDate('');
                                    setEndDate('');
                                    try {
                                        localStorage.removeItem('analyticsDateRange');
                                    } catch (e) {
                                        console.error('Не удалось очистить сохраненный диапазон дат аналитики', e);
                                    }
                                    setIsRestoredFromStorage(false);
                                    return;
                                }

                                const nextStart = values[0].format('YYYY-MM-DD');
                                const nextEnd = values[1].format('YYYY-MM-DD');

                                applyAndPersistDateRange(nextStart, nextEnd);
                            }}
                            disabledDate={(current) => {
                                if (!current) return false;
                                return current.isAfter(dayjs().endOf('day'));
                            }}
                        />
                    </ConfigProvider>
                    <button
                        type="button"
                        className="analytics-table__button"
                        onClick={() => shiftMonth('next')}
                    >
                        ▶
                    </button>
                    <button
                        className="analytics-table__button"
                        onClick={handleLoad}
                        disabled={loading}
                    >
                        {loading ? 'Загрузка...' : 'Показать'}
                    </button>
                </div>
                <div className="analytics-table__search">
                    <input
                        type="text"
                        className={
                            `analytics-table__search-input${
                                isFilterActive ? ' analytics-table__search-input--active' : ''
                            }`
                        }
                        placeholder="Фильтр по кластеру или заводу"
                        value={filterText}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            setFilterText(nextValue);

                            const trimmed = nextValue.trim();
                            const hasValue = !!trimmed;
                            setIsFilterActive(hasValue);

                            try {
                                if (hasValue) {
                                    localStorage.setItem(
                                        'analyticsFilterText',
                                        JSON.stringify({
                                            value: nextValue,
                                            savedAt: Date.now(),
                                        })
                                    );
                                } else {
                                    localStorage.removeItem('analyticsFilterText');
                                }
                            } catch (e) {
                                console.error('Не удалось сохранить фильтр аналитики в localStorage', e);
                            }
                        }}
                    />
                    {filterText && (
                        <button
                            type="button"
                            className="analytics-table__search-clear"
                            onClick={() => {
                                setFilterText('');
                                setIsFilterActive(false);
                                try {
                                    localStorage.removeItem('analyticsFilterText');
                                } catch (e) {
                                    console.error('Не удалось очистить фильтр аналитики из localStorage', e);
                                }
                            }}
                            title="Очистить фильтр"
                        >
                            ×
                        </button>
                    )}
                </div>
                <button
                    className="analytics-table__button analytics-table__button--export"
                    onClick={handleExport}
                    disabled={exporting}
                >
                    {exporting ? 'Выгрузка...' : 'Выгрузить в Excel'}
                </button>
            </div>

            <div className="analytics-table__table-wrapper">
                <table className="analytics-table__table">
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('clusterName')}>
                                Кластер {getSortIndicator('clusterName')}
                            </th>
                            <th onClick={() => requestSort('factoryName')}>
                                Завод {getSortIndicator('factoryName')}
                            </th>
                            <th onClick={() => requestSort('avgWorking')}>
                                Среднее количество работающих за период {getSortIndicator('avgWorking')}
                            </th>
                            <th onClick={() => requestSort('avgNotWorking')}>
                                Среднее количество невыходящих за период {getSortIndicator('avgNotWorking')}
                            </th>
                            <th onClick={() => requestSort('avgVacationShare')}>
                                Средняя доля на межвахте за период, % {getSortIndicator('avgVacationShare')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={5} style={{ textAlign: 'center' }}>
                                    Данные не загружены
                                </td>
                            </tr>
                        )}
                        {rows.map((row, index) => (
                            <tr key={row.factoryId || index}>
                                <td className="analytics-table__cell-text">
                                    {row.clusterName}
                                </td>
                                <td
                                    className="analytics-table__cell-text analytics-table__cell-text--link"
                                    onClick={() => handleFactoryClick(row.factoryId)}
                                >
                                    {row.factoryName}
                                </td>
                                <td
                                    className={
                                        row.avgWorking < 0 ? 'analytics-table__cell-negative' : undefined
                                    }
                                >
                                    {row.avgWorking}
                                </td>
                                <td
                                    className={
                                        row.avgNotWorking < 0 ? 'analytics-table__cell-negative' : undefined
                                    }
                                >
                                    {row.avgNotWorking}
                                </td>
                                <td
                                    className={
                                        row.avgVacationShare < 0
                                            ? 'analytics-table__cell-negative'
                                            : undefined
                                    }
                                >
                                    {row.avgVacationShare} %
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {toastMessage && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    onClose={() => setToastMessage(null)}
                />
            )}
        </div>
    );
};

export default AnalyticsTable;
